import { AnimatePresence } from 'framer-motion';
import { FC, memo, useEffect, useState } from 'react';

import PandaWatermark from '@/assets/icons/cover-images/panda-logo-watermark.png';
import useAlerts from '@/hooks/useAlerts';
import {
  downloadImagesAsZip,
  downloadSelectedImagesAsZip,
  generateImagesInParallel,
  generateSingleImage,
} from '@/utils/coverImages/imageGeneration';
import { getOptimalFontSize as getOptimalFontSizeUtil } from '@/utils/coverImages/textDistribution';
import { replaceQuotes } from '@/utils/helpers';

import { ImageVariation } from '.';
import FloatingBox from '../FloatingBox';
import ImageResults from '../ImageResults';
import PostToArticleButton from '../PostToArticleButton';
import Progress from '../Progress';
import { createWatermarkTagImage } from '../shared';
import VariationsStyles from './VariationsStyles';

interface Props {
  variations?: ImageVariation[];
  images?: string[];
  setImages: React.Dispatch<React.SetStateAction<string[] | undefined>>;
  inArticle?: boolean;
  onReset: () => void;
  isAiGenerated?: boolean;
}

const GeneratedImages: FC<Props> = memo(
  ({ variations, images, setImages, inArticle, onReset, isAiGenerated }) => {
    const alerts = useAlerts();

    const [selectedImages, setSelectedImages] = useState<number[]>([]);
    const [counter, setCounter] = useState(0);
    const [showProgress, setShowProgress] = useState(false);

    const generateImageVariation = async (
      variation: ImageVariation,
    ): Promise<string | undefined> => {
      try {
        return await generateSingleImage(
          () => {
            const imageHolder = document.createElement('div');
            imageHolder.classList.add('image-holder-var24');

            const imageDiv = document.createElement('div');
            imageDiv.classList.add('top-image-var24');
            imageDiv.style.backgroundImage = `url(${variation.leftImageSrc})`;
            imageHolder.appendChild(imageDiv);

            const leftWatermark = createWatermarkTagImage(
              'actual-photo-var24',
              variation.leftWatermarkTag,
              'top',
            );
            if (leftWatermark) imageHolder.appendChild(leftWatermark);

            const bottomHolder = document.createElement('div');
            bottomHolder.classList.add('bottom-holder-var24');

            const answerA = document.createElement('div');
            answerA.classList.add('trivia-answer-var24', 'answer-a-var24');
            answerA.innerHTML = `<span>A:</span> ${variation.answerA}`;
            const answerB = document.createElement('div');
            answerB.classList.add('trivia-answer-var24', 'answer-b-var24');
            answerB.innerHTML = `<span>B:</span> ${variation.answerB}`;
            const answerC = document.createElement('div');
            answerC.classList.add('trivia-answer-var24', 'answer-c-var24');
            answerC.innerHTML = `<span>C:</span> ${variation.answerC}`;
            const answerD = document.createElement('div');
            answerD.classList.add('trivia-answer-var24', 'answer-d-var24');
            answerD.innerHTML = `<span>D:</span> ${variation.answerD}`;

            imageHolder.appendChild(answerA);
            imageHolder.appendChild(answerB);
            imageHolder.appendChild(answerC);
            imageHolder.appendChild(answerD);

            if (variation.answerFontSize) {
              answerA.style.fontSize = `${variation.answerFontSize}px`;
              answerB.style.fontSize = `${variation.answerFontSize}px`;
              answerC.style.fontSize = `${variation.answerFontSize}px`;
              answerD.style.fontSize = `${variation.answerFontSize}px`;
            }

            imageHolder.appendChild(bottomHolder);

            if (variation.question) {
              const rightText = document.createElement('div');
              rightText.classList.add('question-var24');

              let text = '';
              const updatedText = replaceQuotes(variation.question);
              if (variation.quotes) {
                text = `'${updatedText}'`;
              } else {
                text = updatedText;
              }

              const algorithmResult = getOptimalFontSizeUtil(text, {
                maxWidth: 720,
                maxHeight: 80,
                fontFamily: 'CustomArial',
                lineHeight: '1.12',
                minFontSize: 35,
                maxFontSize: 64,
              });

              if (variation.brakes) {
                rightText.innerText = text;
              } else {
                if (
                  typeof algorithmResult === 'object' &&
                  'lines' in algorithmResult
                ) {
                  rightText.innerHTML = algorithmResult.lines.join('<br/>');
                  if (variation.fontSize) {
                    rightText.style.fontSize = `${variation.fontSize}px`;
                  } else {
                    rightText.style.fontSize = `${algorithmResult.fontSize}px`;
                  }
                }
              }

              bottomHolder.appendChild(rightText);
            }

            if (variation.watermark) {
              const pandaWatermark = new Image();
              pandaWatermark.classList.add('watermark-var24');
              pandaWatermark.src = PandaWatermark;
              imageHolder.appendChild(pandaWatermark);
            }

            return imageHolder;
          },
          {
            holderClassName: 'generated-images-holder-var24',
            pixelRatio: 1,
          },
        );
      } catch (e) {
        alerts.error();
        return undefined;
      }
    };

    const generateAllVariations = async () => {
      if (!variations?.length) return;

      setShowProgress(true);
      setCounter(0);

      try {
        const validImages = await generateImagesInParallel(
          variations,
          generateImageVariation,
          (current) => {
            setCounter(current);
          },
        );

        setImages(validImages);
      } finally {
        setShowProgress(false);
        setCounter(0);
      }
    };

    const handleDownloadZip = async (selected: boolean) => {
      if (!images?.length) return;

      if (selected) {
        await downloadSelectedImagesAsZip(images, selectedImages, {
          filename: 'generated-cover-images.zip',
          imagePrefix: 'image-',
        });
      } else {
        await downloadImagesAsZip(images, {
          filename: 'generated-cover-images.zip',
          imagePrefix: 'image-',
        });
      }
    };

    useEffect(() => {
      generateAllVariations();
      // eslint-disable-next-line
    }, [variations]);

    useEffect(() => {
      if (!images?.length) return;
      setSelectedImages(images?.map((_, index) => index));
    }, [images]);

    return (
      <>
        <VariationsStyles />
        <ImageResults
          images={images}
          selectedImages={selectedImages}
          setSelectedImages={setSelectedImages}
          setImages={setImages}
        />
        <AnimatePresence>
          {!!showProgress && !!variations?.length && (
            <Progress
              key={1}
              progressPercent={(counter / variations.length) * 100}
            />
          )}
          {!inArticle ? (
            <>
              {!!images?.length && (
                <FloatingBox
                  key={2}
                  selectedImages={selectedImages}
                  unselectAll={() => setSelectedImages([])}
                  selectAll={() =>
                    setSelectedImages(images?.map((_, index) => index))
                  }
                  onDownload={handleDownloadZip}
                  images={images}
                />
              )}
            </>
          ) : (
            <>
              {!!images?.length && (
                <PostToArticleButton
                  images={images}
                  selectedImages={selectedImages}
                  onReset={onReset}
                  isAiGenerated={isAiGenerated}
                />
              )}
            </>
          )}
        </AnimatePresence>
      </>
    );
  },
);

export default GeneratedImages;
